import { ArrowsPointingInIcon } from '@heroicons/react/24/outline';
import { SerializedConstructor } from '@langchain/core/load/serializable';
import { PlusIcon } from '@langchain/untitled-ui-icons';
import {
  Button,
  FormControl,
  FormLabel,
  Option,
  Select,
  Switch,
} from '@mui/joy';

import { useState } from 'react';

import { ManifestFunctionViewer } from '@/Pages/Playground/components/tools/ToolEditors';
import { ManifestOptions } from '@/Pages/Playground/utils/types';
import { EditInputFunction } from '@/components/ExampleCrudPane/EditInputFunctions';
import { Tooltip } from '@/design-system';
import {
  OpenAIFunction,
  OpenAITool,
  emptyOpenAIFunction,
  emptyOpenAITool,
  isOpenAIFunctionCall,
  isOpenAIFunctions,
  isOpenAITools,
} from '@/utils/ai-tools';
import { cn } from '@/utils/tailwind';

import {
  ManifestEditorProps,
  tableVariantLabelClass,
} from '../../Manifest.utils';
import GenericToolChoiceSelect from './GenericToolChoiceSelect';
import { DEFAULT_TOOL_CHOICE, PROVIDER_TOOL_CHOICES } from './constants';

function ManifestChatOpenAIFunctionEditor(props: {
  value: OpenAIFunction;
  controlled?: boolean;
  onSubmit?: (value: OpenAIFunction) => void;
  onChange?: (value: OpenAIFunction) => void;
  onMinimize?: () => void;
  onCancel?: () => void;
  onRemove: () => void;
  readOnly?: boolean;
}) {
  const [value, setValue] = useState(props.value);

  return (
    <EditInputFunction
      value={props.controlled ? props.value : value}
      onChange={(value) => {
        props.onChange?.(value);
        setValue(value);
      }}
      onRemove={props.onRemove}
      onCancel={props.onCancel}
      onSubmit={props.onSubmit ? () => props.onSubmit?.(value) : undefined}
      readOnly={props.readOnly}
      rightActions={
        <Tooltip title="Minimize" side="top">
          <button
            type="button"
            onClick={props.onMinimize}
            className="flex h-[26px] w-[26px] items-center justify-center rounded-md border border-secondary p-1 transition-all hover:bg-secondary-hover active:bg-secondary"
          >
            <ArrowsPointingInIcon className="h-4 w-4" />
          </button>
        </Tooltip>
      }
    />
  );
}

export function ManifestChatOpenAIFunction(props: {
  value: OpenAIFunction;
  onRemove: () => void;
  onChange?: (value: OpenAIFunction) => void;
  onSubmit?: (value: OpenAIFunction) => void;
  readOnly?: boolean;
}) {
  const fn = props.value;
  const [open, setOpen] = useState(!fn.name);

  if (!open) {
    return (
      <div className="h-[40px]">
        <ManifestFunctionViewer
          onOpen={() => setOpen(true)}
          onRemove={props.onRemove}
          name={props.value?.name ?? undefined}
          defaultName="Untitled function"
          readOnly={props.readOnly}
        />
      </div>
    );
  }

  return (
    <div className="grow [min-width:min(100%,700px)]">
      <ManifestChatOpenAIFunctionEditor
        value={fn}
        onRemove={props.onRemove}
        onChange={props.onChange}
        onMinimize={() => setOpen(false)}
        controlled
        readOnly={props.readOnly}
      />
    </div>
  );
}

function ManifestChatOpenAIFunctionsEditor(
  props: ManifestEditorProps<SerializedConstructor>
) {
  const functions = isOpenAIFunctions(props.options.functions)
    ? props.options.functions
    : [];

  function handleChange(functions: Array<OpenAIFunction> | null) {
    props.onOptionsChange({
      ...props.options,
      tools: undefined,
      tool_choice: undefined,
      functions: functions?.length ? functions : undefined,
      function_call: functions?.length
        ? props.options.function_call
        : undefined,
    });
  }

  function handleCallChange(value: unknown) {
    if (typeof value === 'string') {
      props.onOptionsChange({
        ...props.options,
        function_call: value.startsWith('func:')
          ? { name: value.slice('func:'.length) }
          : value,
        tool_choice: undefined,
      });
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-[1fr,auto] items-center gap-2">
        <Select
          value={
            isOpenAIFunctionCall(props.options.function_call)
              ? `func:${props.options.function_call.name}`
              : undefined
          }
          defaultValue="auto"
          onChange={(_, value) => handleCallChange(value)}
          disabled={props.readOnly || functions.length === 0}
        >
          <Option value="auto">Functions auto-selected by LLM</Option>
          <Option value="none">Don't use any functions</Option>
          {functions.map((func, index) => (
            <Option key={index} value={`func:${func.name}`}>
              {func.name || 'Unnamed function'}
            </Option>
          ))}
        </Select>
        <Button
          variant="outlined"
          sx={(theme) => ({ bg: theme.palette.neutral.solidBg })}
          color="neutral"
          onClick={() => handleChange([...functions, emptyOpenAIFunction()])}
          startDecorator={<PlusIcon className="size-4" />}
        >
          Add
        </Button>
      </div>

      {props.value.kwargs?.model === 'gpt-4-vision-preview' &&
        functions.length > 0 && (
          <div className="rounded-lg border border-brand p-3 text-sm text-brand-green-400 text-secondary">
            <strong className="font-semibold">Note:</strong> This model does not
            support functions and will result in an error.
          </div>
        )}

      <div className="flex flex-col gap-3">
        {functions.map((value, index) => {
          return (
            <ManifestChatOpenAIFunction
              key={index}
              value={value}
              onChange={(item) => {
                handleChange(functions.map((v, i) => (i === index ? item : v)));
              }}
              onRemove={() => {
                handleChange(functions.filter((_, i) => i !== index));
              }}
              readOnly={props.readOnly}
            />
          );
        })}
      </div>
    </div>
  );
}

function ManifestChatOpenAIToolsEditor(
  props: ManifestEditorProps<SerializedConstructor> & {
    disableSpecificToolChoice?: boolean;
  }
) {
  const tools = isOpenAITools(props.options.tools) ? props.options.tools : [];

  function handleChange(tools: Array<OpenAITool> | null) {
    props.onOptionsChange({
      ...props.options,
      functions: undefined,
      function_call: undefined,
      tools: tools?.length ? tools : undefined,
      tool_choice: tools?.length ? props.options.tool_choice : undefined,
    });
  }

  return (
    <div className="flex flex-col gap-2" key={props.provider}>
      {!props.readOnly && (
        <div className="grid grid-cols-[1fr,auto] items-center gap-2">
          {props.provider && (
            <GenericToolChoiceSelect
              {...props}
              tools={tools}
              toolChoices={
                PROVIDER_TOOL_CHOICES[props.provider] ?? [DEFAULT_TOOL_CHOICE]
              }
              toolPrefix={
                ['AzureChatOpenAI', 'ChatAnthropic'].includes(props.provider)
                  ? 'tool'
                  : 'func'
              }
              disableSpecificToolChoice={
                !['AzureChatOpenAI', 'ChatAnthropic'].includes(props.provider)
                  ? props.disableSpecificToolChoice
                  : undefined
              }
              provider={props.provider}
            />
          )}
          <Button
            variant="outlined"
            data-testid="add-tool-button"
            sx={(theme) => ({ bg: theme.palette.neutral.solidBg })}
            color="neutral"
            onClick={() => handleChange([...tools, emptyOpenAITool()])}
            startDecorator={<PlusIcon className="size-4" />}
            size="sm"
          >
            Add
          </Button>
        </div>
      )}

      {props.value.kwargs?.model === 'gpt-4-vision-preview' &&
        tools.length > 0 && (
          <div className="rounded-lg border border-brand p-3 text-sm text-brand-green-400 text-secondary">
            <strong className="font-semibold">Note:</strong> This model does not
            support tools and will result in an error.
          </div>
        )}

      <div className="flex flex-col gap-3">
        {tools.map((value, index) => {
          return (
            <ManifestChatOpenAIFunction
              key={index}
              value={value.function}
              onChange={(value) =>
                handleChange(
                  tools.map((v, i) =>
                    i === index ? { type: 'function', function: value } : v
                  )
                )
              }
              onRemove={() => {
                handleChange(tools.filter((_, i) => i !== index));
              }}
              readOnly={props.readOnly}
            />
          );
        })}
      </div>
    </div>
  );
}

export function ManifestChatOpenAIFunctions(
  props: ManifestEditorProps<SerializedConstructor> & {
    onlyLegacyFunctions?: boolean;
    hideTitle?: boolean;
    disableSpecificToolChoice?: boolean;
  }
) {
  const functions = isOpenAIFunctions(props.options.functions)
    ? props.options.functions
    : [];

  // not all azure openai versions will support tools
  if (functions.length > 0 || props.onlyLegacyFunctions) {
    return (
      <div className="flex flex-col gap-2">
        {props.variant === 'table' ? (
          <div className={cn(tableVariantLabelClass, 'pt-6')}>Functions</div>
        ) : (
          <FormLabel>Tools</FormLabel>
        )}
        <ManifestChatOpenAIFunctionsEditor {...props} />
      </div>
    );
  }

  const tools = isOpenAITools(props.options?.tools);

  return (
    <div className="flex flex-col gap-2">
      {!props.hideTitle &&
        !(props.readOnly && !tools) &&
        (props.variant === 'table' ? (
          <div className={cn(tableVariantLabelClass, 'pt-6')}>Tools</div>
        ) : (
          <FormLabel>Tools</FormLabel>
        ))}
      <ManifestChatOpenAIToolsEditor
        {...props}
        disableSpecificToolChoice={props.disableSpecificToolChoice}
      />
    </div>
  );
}

export function ManifestChatOpenAIJsonMode(
  props: ManifestEditorProps<SerializedConstructor>
) {
  const checked =
    !!props.options &&
    typeof props.options.response_format === 'object' &&
    props.options.response_format != null &&
    'type' in props.options.response_format &&
    props.options.response_format.type === 'json_object';

  return (
    <FormControl orientation="horizontal" className="items-center">
      <span className="mr-2 text-sm font-medium">JSON mode</span>

      <Switch
        checked={checked}
        readOnly={props.readOnly}
        onChange={(e) => {
          props.onOptionsChange({
            ...props.options,
            response_format: e.target.checked
              ? { type: 'json_object' }
              : undefined,
          });
        }}
      />
    </FormControl>
  );
}

export function ManifestOpenAIParallelToolCalls({
  options,
  onOptionsChange,
}: {
  options: ManifestOptions;
  onOptionsChange: (options: ManifestOptions) => void;
}) {
  // default to true if undefined
  const parallelToolCalls = options?.parallel_tool_calls ?? true;
  const disabled = !options?.tools?.length;

  return (
    <FormControl orientation="horizontal">
      <span className="my-auto flex-1 text-sm font-medium">
        Parallel Tool Calls
      </span>
      <Tooltip
        title={
          disabled ? 'No tools available. Define a tool to enable' : undefined
        }
      >
        <div>
          <Switch
            checked={disabled ? false : parallelToolCalls}
            disabled={disabled}
            onChange={() => {
              onOptionsChange({
                ...options,
                parallel_tool_calls: !parallelToolCalls,
              });
            }}
          />
        </div>
      </Tooltip>
    </FormControl>
  );
}
